'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { track } from '@vercel/analytics';

interface BooksFilteringClientProps {
  children: React.ReactNode;
  sourceUrl?: string;
}

export default function BooksFilteringClient({ children, sourceUrl }: BooksFilteringClientProps) {
  const [isClient, setIsClient] = useState(false);
  const announcementRef = useRef<HTMLDivElement>(null);

  // Get all book elements
  const getBookElements = useCallback(() => {
    return Array.from(document.querySelectorAll('[data-book-item]')) as HTMLElement[];
  }, []);

  // Get filter options from the server-rendered select elements
  const getFilterOptions = useCallback(() => {
    const categorySelect = document.getElementById('filter-category') as HTMLSelectElement;
    const experienceSelect = document.getElementById('filter-experience') as HTMLSelectElement;
    const languageSelect = document.getElementById('filter-language') as HTMLSelectElement;

    return {
      category: categorySelect?.value || '',
      experienceLevel: experienceSelect?.value || '',
      language: languageSelect?.value || ''
    };
  }, []);

  // Filter books based on current filters
  const filterBooks = useCallback(() => {
    const bookElements = getBookElements();
    const currentFilters = getFilterOptions();
    
    let visibleCount = 0;

    bookElements.forEach((bookElement) => {
      const bookData = bookElement.dataset;
      let shouldShow = true;

      // Category filter
      if (currentFilters.category && bookData.category) {
        const bookCategories = bookData.category.split(',').map(cat => cat.trim());
        if (!bookCategories.includes(currentFilters.category)) {
          shouldShow = false;
        }
      }

      // Experience level filter
      if (currentFilters.experienceLevel && bookData.experience) {
        const bookExperience = bookData.experience.split(',').map(exp => exp.trim());
        if (!bookExperience.includes(currentFilters.experienceLevel)) {
          shouldShow = false;
        }
      }

      // Language filter
      if (currentFilters.language && bookData.language) {
        const bookLanguages = bookData.language.split(',').map(lang => lang.trim().toLowerCase());
        if (!bookLanguages.includes(currentFilters.language.toLowerCase())) {
          shouldShow = false;
        }
      }

      // Show/hide book
      if (shouldShow) {
        bookElement.style.display = '';
        bookElement.classList.remove('hidden');
        visibleCount++;
      } else {
        bookElement.style.display = 'none';
        bookElement.classList.add('hidden');
      }
    });

    // Update results count
    const resultsRegion = document.getElementById('books-results-region');
    if (resultsRegion) {
      resultsRegion.setAttribute('aria-label', `${visibleCount} books found`);
    }

    // Announce results to screen readers
    if (announcementRef.current) {
      announcementRef.current.textContent = `${visibleCount} books found`;
    }
  }, [getBookElements, getFilterOptions]);

  // Update URL with current filter values
  const updateUrlParams = useCallback(() => {
    const categorySelect = document.getElementById('filter-category') as HTMLSelectElement;
    const experienceSelect = document.getElementById('filter-experience') as HTMLSelectElement;
    const languageSelect = document.getElementById('filter-language') as HTMLSelectElement;
    
    const params = new URLSearchParams();
    
    if (categorySelect?.value) {
      params.set('category', categorySelect.value);
    }
    if (experienceSelect?.value) {
      params.set('experience', experienceSelect.value);
    }
    if (languageSelect?.value) {
      params.set('language', languageSelect.value);
    }
    
    // Preserve the hash if present
    const hash = window.location.hash;
    const newUrl = params.toString() 
      ? `${window.location.pathname}?${params.toString()}${hash}`
      : `${window.location.pathname}${hash}`;
    
    window.history.replaceState({}, '', newUrl);
  }, []);

  // Handle filter changes
  const handleFilterChange = useCallback((filterType: string, value: string) => {
    // Announce filter changes to screen readers
    if (announcementRef.current) {
      const filterName = filterType === 'category' ? 'category' : 
                        filterType === 'experienceLevel' ? 'experience level' :
                        filterType === 'language' ? 'language' : filterType;
      announcementRef.current.textContent = `Filter changed to ${value || 'all'} ${filterName}`;
    }

    // Update URL with new filter values
    updateUrlParams();

    // Apply filters
    setTimeout(filterBooks, 0);
  }, [updateUrlParams, filterBooks]);

  // Ensure we're on the client side and read query parameters
  useEffect(() => {
    setIsClient(true);
    
    // Parse URL - handle both ?param#hash and #hash?param formats
    let searchParams = window.location.search;
    let hash = window.location.hash;
    
    // Handle incorrect format: #books?category=X (extract params from hash)
    if (hash.includes('?')) {
      const hashParts = hash.split('?');
      hash = hashParts[0]; // Keep only the hash part
      if (!searchParams) {
        searchParams = '?' + hashParts[1]; // Move params to search
      }
    }
    
    const urlParams = new URLSearchParams(searchParams);
    const categoryParam = urlParams.get('category');
    const experienceParam = urlParams.get('experience');
    const languageParam = urlParams.get('language');
    const hasParams = categoryParam || experienceParam || languageParam;
    
    // Normalize URL if needed (fix #books?param format)
    if (window.location.hash.includes('?')) {
      const cleanHash = hash;
      const params = urlParams.toString();
      const newUrl = params 
        ? `${window.location.pathname}?${params}${cleanHash}`
        : `${window.location.pathname}${cleanHash}`;
      window.history.replaceState({}, '', newUrl);
    }
    
    // Apply filters and scroll to anchor
    if (hasParams || hash === '#books') {
      setTimeout(() => {
        const categorySelect = document.getElementById('filter-category') as HTMLSelectElement;
        const experienceSelect = document.getElementById('filter-experience') as HTMLSelectElement;
        const languageSelect = document.getElementById('filter-language') as HTMLSelectElement;
        
        if (categoryParam && categorySelect) {
          categorySelect.value = categoryParam;
        }
        if (experienceParam && experienceSelect) {
          experienceSelect.value = experienceParam;
        }
        if (languageParam && languageSelect) {
          languageSelect.value = languageParam.toLowerCase();
        }
        
        // Trigger filtering with URL params
        if (hasParams) {
          filterBooks();
        }
        
        // Scroll to #books anchor if present
        if (hash === '#books') {
          setTimeout(() => {
            const booksSection = document.getElementById('books');
            if (booksSection) {
              booksSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, hasParams ? 100 : 0); // Add small delay if we applied filters
        }
      }, 150);
    }
  }, [filterBooks]);

  // Track book clicks
  const handleBookClick = (event: Event) => {
    const target = event.target as HTMLElement;
    const bookElement = target.closest('[data-book-item]') as HTMLElement;
    
    if (bookElement) {
      const bookTitle = bookElement.querySelector('h3')?.textContent || 'Unknown Book';
      
      track('Book Recommendation Click', {
        book: bookTitle || "",
      url: sourceUrl || ""
      });
    }
  };

  // Set up event listeners for filter changes
  useEffect(() => {
    if (!isClient) return;

    let timeoutId: NodeJS.Timeout;
    let cleanup: (() => void) | undefined;

    const setupFiltering = () => {
      const categorySelect = document.getElementById('filter-category') as HTMLSelectElement;
      const experienceSelect = document.getElementById('filter-experience') as HTMLSelectElement;
      const languageSelect = document.getElementById('filter-language') as HTMLSelectElement;

      if (!categorySelect || !experienceSelect || !languageSelect) {
        timeoutId = setTimeout(setupFiltering, 100);
        return;
      }

      const handleCategoryChange = () => {
        handleFilterChange('category', categorySelect.value);
      };

      const handleExperienceChange = () => {
        handleFilterChange('experienceLevel', experienceSelect.value);
      };

      const handleLanguageChange = () => {
        handleFilterChange('language', languageSelect.value);
      };

      // Add event listeners
      categorySelect.addEventListener('change', handleCategoryChange);
      experienceSelect.addEventListener('change', handleExperienceChange);
      languageSelect.addEventListener('change', handleLanguageChange);

      // Add book click tracking
      const booksContainer = document.getElementById('books-results-region');
      if (booksContainer) {
        booksContainer.addEventListener('click', handleBookClick);
      }

      // Initial filter application
      setTimeout(() => {
        filterBooks();
      }, 50);

      // Cleanup function
      cleanup = () => {
        categorySelect.removeEventListener('change', handleCategoryChange);
        experienceSelect.removeEventListener('change', handleExperienceChange);
        languageSelect.removeEventListener('change', handleLanguageChange);
        if (booksContainer) {
          booksContainer.removeEventListener('click', handleBookClick);
        }
      };
    };

    // Start setup
    setupFiltering();

    // Cleanup
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (cleanup) {
        cleanup();
      }
    };
  }, [isClient, filterBooks, handleFilterChange]);

  return (
    <>
      {/* Screen reader announcements */}
      <div 
        ref={announcementRef}
        aria-live="polite" 
        aria-atomic="true"
        className="sr-only"
      />
      
      {children}
    </>
  );
}
